import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Menu,
  Plus,
  Crown,
  Shield,
  Sun,
  Moon,
  Monitor,
  Settings
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useIsMobile } from '../../hooks/useIsMobile'
import NotificationCenter from '../notifications/NotificationCenter'
import Breadcrumb from './Breadcrumb'
import { motion } from 'framer-motion'
import * as ReactHelmetAsync from 'react-helmet-async'
const { Helmet } = ReactHelmetAsync

interface HeaderProps {
  onMenuClick: () => void
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile, signOut, isAdmin } = useAuth()
  const { isPremium, isEscolar, subscriptionPhase } = useSubscription()
  const { theme, setTheme } = useTheme()
  const { isMobile } = useIsMobile()
  const [showUserMenu, setShowUserMenu] = React.useState(false)

  // No mobile o header fica enxuto — a criação de avaliação vive no dashboard
  // e no editor; o CTA "Nova Avaliação" só aparece no desktop.
  const hideNewAssessment = isMobile

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (error) {
      // Error handled silently
    }
  }

  const getPageTitle = () => {
    const path = location.pathname
    const titles: { [key: string]: string } = {
      '/app': 'Dashboard',
      '/app/questions': 'Banco de Questões',
      '/app/editor': 'Editor de Avaliações',
      '/app/assessments': 'Minhas Avaliações',
      '/app/templates': 'Modelos Prontos',
      '/app/banco-avaliacoes': 'Banco de Avaliações',
      '/app/analytics': 'Analytics',
      '/app/settings': 'Configurações',
      '/app/billing': 'Planos e Cobrança',
      '/admin': 'Painel Administrativo',
      '/admin/users': 'Gerenciamento de Usuários',
      '/admin/questions': 'Gerenciamento de Questões',
    }
    const matchingPath = Object.keys(titles).sort((a, b) => b.length - a.length).find(key => path.startsWith(key));
    return matchingPath ? titles[matchingPath] : 'Atividade Pronta';
  }

  React.useEffect(() => {
    document.title = `${getPageTitle()} | Atividade Pronta`;
  }, [location.pathname]);

  const pageTitle = `${getPageTitle()} | Atividade Pronta`;
  const pageDescription = 'A plataforma completa para professores criarem avaliações profissionais, com banco de questões completo, templates e geração de PDFs otimizados.';

  // Get effective theme (resolve 'system' to actual theme)
  const getEffectiveTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme
  }

  const toggleTheme = () => {
    const effectiveTheme = getEffectiveTheme()
    if (effectiveTheme === 'light') {
      setTheme('dark')
    } else {
      setTheme('light')
    }
  }

  const getThemeIcon = () => {
    const effectiveTheme = getEffectiveTheme()
    if (effectiveTheme === 'light') return <Sun className="w-5 h-5" />
    return <Moon className="w-5 h-5" />
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="https://atividadepronta.com.br/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://atividadepronta.com.br/og-image.png" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <header className="h-[52px] bg-surface dark:bg-ink-700 border-b border-cream-400 dark:border-ink-500 flex items-center w-full sticky top-0 z-40">
        <div className="flex items-center justify-between w-full px-5 lg:px-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={onMenuClick}
              className="p-2 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors lg:hidden"
            >
              <Menu className="w-5 h-5 text-ink-500 dark:text-ink-300" />
            </button>

            <Breadcrumb />
          </div>

          <div className="flex items-center space-x-2 lg:space-x-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-ink-500 dark:text-ink-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors"
              title={`Alternar para tema ${theme === 'light' ? 'escuro' : 'claro'}`}
            >
              {getThemeIcon()}
            </button>

            {/* New Assessment Button */}
            {!hideNewAssessment && (
              <button
                onClick={() => navigate('/app/editor')}
                className="flex items-center space-x-2 bg-terra-500 hover:bg-terra-700 text-cream-50 px-3 lg:px-4 py-2 rounded-caderno-sm transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Nova Avaliação</span>
              </button>
            )}

            {/* Admin Panel Button (if admin) */}
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2 bg-blood-500 hover:bg-blood-500/90 text-cream-50 px-3 lg:px-4 py-2 rounded-caderno-sm transition-colors text-sm font-medium"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Admin</span>
              </button>
            )}

            {/* Notifications */}
            <NotificationCenter />

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 p-2 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors"
              >
                <div className="text-right hidden lg:block">
                  <p className="text-sm font-medium text-ink-900 dark:text-cream-50 flex items-center space-x-1">
                    <span>{profile?.nome || user?.email}</span>
                    {(isPremium || isEscolar) && (
                      <Crown className="w-4 h-4 text-ochre-500" />
                    )}
                  </p>
                  <p className="text-xs text-ink-400 dark:text-ink-300">
                    {isEscolar
                      ? 'Plano Escolar'
                      : subscriptionPhase === 'trialing'
                        ? 'Premium · Trial'
                        : isPremium
                          ? 'Premium'
                          : 'Sem Assinatura'}
                  </p>
                </div>
                <div className="w-8 h-8 bg-cream-300 dark:bg-ink-700 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-ink-500 dark:text-cream-300">
                    {profile?.nome?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                </div>
              </button>

              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-56 bg-surface dark:bg-ink-700 rounded-caderno-sm shadow-caderno-lg border border-cream-400 dark:border-ink-500 py-2 z-50"
                >
                  <div className="px-4 py-2 border-b border-cream-400 dark:border-ink-500">
                    <p className="font-medium text-ink-900 dark:text-cream-50">{profile?.nome || user?.email}</p>
                    <p className="text-sm text-ink-400 dark:text-ink-300">{profile?.escola || 'Sem escola'}</p>
                  </div>

                  <button
                    onClick={() => {
                      navigate('/app/settings')
                      setShowUserMenu(false)
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-cream-50 dark:hover:bg-ink-700 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-ink-400 dark:text-ink-300" />
                    <span className="text-sm text-ink-700 dark:text-cream-300">Configurações</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/app/billing')
                      setShowUserMenu(false)
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-cream-50 dark:hover:bg-ink-700 transition-colors"
                  >
                    <Crown className="w-4 h-4 text-ink-400 dark:text-ink-300" />
                    <span className="text-sm text-ink-700 dark:text-cream-300">Planos e Cobrança</span>
                  </button>

                  {isAdmin && (
                    <button
                      onClick={() => {
                        navigate('/admin')
                        setShowUserMenu(false)
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-cream-50 dark:hover:bg-ink-700 transition-colors"
                    >
                      <Shield className="w-4 h-4 text-blood-500" />
                      <span className="text-sm text-ink-700 dark:text-cream-300">Painel Admin</span>
                    </button>
                  )}

                  <hr className="my-2 border-cream-400 dark:border-ink-500" />

                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-cream-50 dark:hover:bg-ink-700 transition-colors text-blood-500"
                  >
                    <span className="text-sm">Sair</span>
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header